import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiUpload,
  FiFile,
  FiImage,
  FiDownload,
  FiBookmark,
  FiClock,
  FiUser,
  FiCheckCircle,
  FiAlertCircle,
  FiX,
  FiSave,
  FiMessageCircle,
  FiCalendar
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Notes = () => {
  const { getAccessToken, refreshToken, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [todos, setTodos] = useState([]);
  const [files, setFiles] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingTodo, setEditingTodo] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Form states
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    is_pinned: false
  });

  const [todoForm, setTodoForm] = useState({
    task: '',
    description: '',
    assigned_to: '',
    priority: 'normal',
    due_date: ''
  });

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    normal: 'bg-secondary-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800'
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-secondary-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800'
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    let token = getAccessToken();
    
    if (!token) {
      throw new Error('No access token available');
    }

    const requestOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (response.status === 401) {
        // Token might be expired, try to refresh
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          // Retry with new token
          token = getAccessToken();
          requestOptions.headers['Authorization'] = `Bearer ${token}`;
          return await fetch(url, requestOptions);
        }
        throw new Error('Authentication failed');
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [notesRes, todosRes, filesRes, usersRes] = await Promise.all([
        makeAuthenticatedRequest(`${API_URL}/cms/admin/notes`),
        makeAuthenticatedRequest(`${API_URL}/cms/admin/todos`),
        makeAuthenticatedRequest(`${API_URL}/cms/admin/files`),
        makeAuthenticatedRequest(`${API_URL}/cms/admin/users`)
      ]);

      const notesData = await notesRes.json();
      const todosData = await todosRes.json();
      const filesData = await filesRes.json();
      const usersData = await usersRes.json();


      setNotes(notesData.notes || []);
      setTodos(todosData.todos || []);
      setFiles(filesData.files || []);
      setAdminUsers(usersData.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingNote ? 'PUT' : 'POST';
      const url = editingNote
        ? `${API_URL}/cms/admin/notes/${editingNote.id}`
        : `${API_URL}/cms/admin/notes`;
      
      const response = await makeAuthenticatedRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(noteForm)
      });

      if (response.ok) {
        setShowNoteModal(false);
        setEditingNote(null);
        setNoteForm({ title: '', content: '', priority: 'normal', is_pinned: false });
        fetchData();
      } else {
        const errorData = await response.json();
        console.error('Error saving note:', errorData);
        alert('Error saving note: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Error saving note: ' + error.message);
    }
  };

  const handleTodoSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingTodo ? 'PUT' : 'POST';
      const url = editingTodo
        ? `${API_URL}/cms/admin/todos/${editingTodo.id}`
        : `${API_URL}/cms/admin/todos`;
      
      const response = await makeAuthenticatedRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(todoForm)
      });

      if (response.ok) {
        setShowTodoModal(false);
        setEditingTodo(null);
        setTodoForm({ task: '', description: '', assigned_to: '', priority: 'normal', due_date: '' });
        fetchData();
      } else {
        const errorData = await response.json();
        console.error('Error saving todo:', errorData);
        alert('Error saving todo: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving todo:', error);
      alert('Error saving todo: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', `Uploaded by admin`);

    try {
      setUploading(true);
      const response = await makeAuthenticatedRequest(`${API_URL}/cms/admin/files`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json();
        console.error('Error uploading file:', errorData);
        alert('Error uploading file: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await makeAuthenticatedRequest(`${API_URL}/cms/admin/notes/${id}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note: ' + error.message);
    }
  };

  const deleteTodo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;
    
    try {
      await makeAuthenticatedRequest(`${API_URL}/cms/admin/todos/${id}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting todo:', error);
      alert('Error deleting todo: ' + error.message);
    }
  };

  const deleteFile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await makeAuthenticatedRequest(`${API_URL}/cms/admin/files/${id}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file: ' + error.message);
    }
  };

  const updateTodoStatus = async (id, status) => {
    try {
      await makeAuthenticatedRequest(`${API_URL}/cms/admin/todos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating todo status:', error);
      alert('Error updating todo status: ' + error.message);
    }
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_URL}/cms/admin/files/${fileId}/download`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file: ' + error.message);
    }
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title,
      content: note.content,
      priority: note.priority,
      is_pinned: note.is_pinned
    });
    setShowNoteModal(true);
  };

  const openEditTodo = (todo) => {
    setEditingTodo(todo);
    setTodoForm({
      task: todo.task,
      description: todo.description || '',
      assigned_to: todo.assigned_to || '',
      priority: todo.priority,
      due_date: todo.due_date ? todo.due_date.split('T')[0] : ''
    });
    setShowTodoModal(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Please log in to access Notes & Updates</p>
          <button 
            onClick={() => window.location.href = '/login'} 
            className="btn btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.notes')}</h1>
          <p className="text-gray-600">{t('cms.notes.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'notes', label: 'Notes', count: notes.length },
            { id: 'todos', label: 'Todo List', count: todos.filter(t => t.status !== 'completed').length },
            { id: 'files', label: 'Files', count: files.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="space-y-6">
          {/* Notes Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Admin Notes</h2>
            <button
              onClick={() => {
                setEditingNote(null);
                setNoteForm({ title: '', content: '', priority: 'normal', is_pinned: false });
                setShowNoteModal(true);
              }}
              className="btn btn-primary flex items-center space-x-2"
            >
              <FiPlus className="w-4 h-4" />
              <span>{t('cms.notes.addNote')}</span>
            </button>
          </div>

          {/* Notes List */}
          <div className="grid gap-4">
            {notes.map((note) => (
              <div key={note.id} className={`bg-white rounded-lg shadow p-6 ${note.is_pinned ? 'ring-2 ring-yellow-400' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {Boolean(note.is_pinned) && <FiBookmark className="w-4 h-4 text-yellow-500" />}
                      <h3 className="font-semibold text-gray-900">{note.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[note.priority]}`}>
                        {t(`cms.notes.${note.priority}`)}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <div className="flex items-center space-x-1">
                        <FiUser className="w-4 h-4" />
                        <span>{note.author_full_name || note.author_name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FiClock className="w-4 h-4" />
                        <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      </div>
                      {note.comment_count > 0 && (
                        <div className="flex items-center space-x-1">
                          <FiMessageCircle className="w-4 h-4" />
                          <span>{note.comment_count} comments</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditNote(note)}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                    >
                      <FiEdit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {notes.length === 0 && (
              <div className="text-center py-12">
                <FiEdit3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{t('cms.notes.noNotes')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Todos Tab */}
      {activeTab === 'todos' && (
        <div className="space-y-6">
          {/* Todos Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Todo List</h2>
            <button
              onClick={() => {
                setEditingTodo(null);
                setTodoForm({ task: '', description: '', assigned_to: '', priority: 'normal', due_date: '' });
                setShowTodoModal(true);
              }}
              className="btn btn-primary flex items-center space-x-2"
            >
              <FiPlus className="w-4 h-4" />
              <span>{t('cms.notes.addTodo')}</span>
            </button>
          </div>

          {/* Todos List */}
          <div className="grid gap-4">
            {todos.map((todo) => (
              <div key={todo.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <button
                        onClick={() => updateTodoStatus(todo.id, todo.status === 'completed' ? 'pending' : 'completed')}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          todo.status === 'completed'
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 hover:border-green-500'
                        }`}
                      >
                        {todo.status === 'completed' && <FiCheckCircle className="w-3 h-3" />}
                      </button>
                      <h3 className={`font-medium ${todo.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {todo.task}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[todo.priority]}`}>
                        {t(`cms.notes.${todo.priority}`)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[todo.status]}`}>
                        {todo.status === 'in_progress' ? t('cms.notes.inProgress') : todo.status === 'completed' ? t('cms.notes.completed') : todo.status.replace('_', ' ')}
                      </span>
                    </div>
                    {todo.description && (
                      <p className="text-gray-600 mb-2 ml-7">{todo.description}</p>
                    )}
                    <div className="flex items-center text-sm text-gray-500 space-x-4 ml-7">
                      <div className="flex items-center space-x-1">
                        <FiUser className="w-4 h-4" />
                        <span>By {todo.created_by_full_name || todo.created_by_name}</span>
                      </div>
                      {todo.assigned_to_name && (
                        <div className="flex items-center space-x-1">
                          <FiUser className="w-4 h-4" />
                          <span>Assigned to {todo.assigned_to_full_name || todo.assigned_to_name}</span>
                        </div>
                      )}
                      {todo.due_date && (
                        <div className="flex items-center space-x-1">
                          <FiCalendar className="w-4 h-4" />
                          <span>Due {new Date(todo.due_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      {todo.status === 'completed' && todo.completed_by_name && (
                        <div className="flex items-center space-x-1">
                          <FiCheckCircle className="w-4 h-4 text-green-500" />
                          <span>Completed by {todo.completed_by_full_name || todo.completed_by_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditTodo(todo)}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100"
                    >
                      <FiEdit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteTodo(todo.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {todos.length === 0 && (
              <div className="text-center py-12">
                <FiCheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{t('cms.notes.noTodos')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Files Tab */}
      {activeTab === 'files' && (
        <div className="space-y-6">
          {/* Files Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Uploaded Files</h2>
            <div className="flex items-center space-x-4">
              <label className="btn btn-primary flex items-center space-x-2 cursor-pointer">
                <FiUpload className="w-4 h-4" />
                <span>{uploading ? 'Uploading...' : 'Upload File'}</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          {/* Files List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((file) => (
              <div key={file.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center space-x-3 mb-3">
                  {file.file_type === 'pdf' ? (
                    <FiFile className="w-8 h-8 text-red-500" />
                  ) : (
                    <FiImage className="w-8 h-8 text-secondary-500" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{file.original_name}</h3>
                    <p className="text-sm text-gray-500">
                      {(file.file_size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mb-3">
                  <div className="flex items-center space-x-1">
                    <FiUser className="w-3 h-3" />
                    <span>{file.uploaded_by_full_name || file.uploaded_by_name}</span>
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    <FiClock className="w-3 h-3" />
                    <span>{new Date(file.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => downloadFile(file.id, file.original_name)}
                    className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm"
                  >
                    <FiDownload className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => deleteFile(file.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {files.length === 0 && (
              <div className="col-span-full text-center py-12">
                <FiFile className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No files uploaded yet. Upload your first file!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingNote ? t('cms.notes.editNote') : t('cms.notes.addNote')}
              </h3>
              <button
                onClick={() => setShowNoteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleNoteSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.notes.priority')}</label>
                  <select
                    value={noteForm.priority}
                    onChange={(e) => setNoteForm({ ...noteForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="low">{t('cms.notes.low')}</option>
                    <option value="normal">{t('cms.notes.normal')}</option>
                    <option value="high">{t('cms.notes.high')}</option>
                    <option value="urgent">{t('cms.notes.urgent')}</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={noteForm.is_pinned}
                      onChange={(e) => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{t('cms.notes.pinNote')}</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowNoteModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('cms.common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <FiSave className="w-4 h-4" />
                  <span>{t('cms.common.save')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Todo Modal */}
      {showTodoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingTodo ? t('cms.notes.editNote') : t('cms.notes.addTodo')}
              </h3>
              <button
                onClick={() => setShowTodoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleTodoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
                <input
                  type="text"
                  value={todoForm.task}
                  onChange={(e) => setTodoForm({ ...todoForm, task: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={todoForm.description}
                  onChange={(e) => setTodoForm({ ...todoForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.notes.priority')}</label>
                  <select
                    value={todoForm.priority}
                    onChange={(e) => setTodoForm({ ...todoForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="low">{t('cms.notes.low')}</option>
                    <option value="normal">{t('cms.notes.normal')}</option>
                    <option value="high">{t('cms.notes.high')}</option>
                    <option value="urgent">{t('cms.notes.urgent')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.notes.assignTo')}</label>
                  <select
                    value={todoForm.assigned_to}
                    onChange={(e) => setTodoForm({ ...todoForm, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select user</option>
                    {adminUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name || user.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.notes.dueDate')}</label>
                <input
                  type="date"
                  value={todoForm.due_date}
                  onChange={(e) => setTodoForm({ ...todoForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowTodoModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('cms.common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <FiSave className="w-4 h-4" />
                  <span>{t('cms.common.save')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;